import { useState, useReducer, useMemo, memo, useEffect } from "react";
import {
  Loader2,
  XCircle,
  Upload,
  CheckCircle,
  Save,
  X,
  ChevronDown,
  ChevronUp,
  Sparkles,
  RefreshCw,
  MapPin,
  Eye,
  AlertTriangle,
  Camera,
  Coffee,
  Compass,
  Utensils,
  Map,
  AlertCircle,
  Wallet,
} from "lucide-react";
import RatingStars from "../components/RatingStars";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import AddComment from "./AddComment";
import CommentList from "./CommentList";
import AudioNarrationCard from "./AudioPlayer";
import Gallery from "./gallery";
import PredictionResult from "./Report";
import NearbyPlacesMap from "./NearbyPlacesMap";
import SocialPlacesMap from "./SocialMap";
import LoadingBar from "./LoadingBar";
import MagnifyingGlassAnimation from "./MagnifyingGlassAnimation";
import VoiceNarration from "./VoiceNarration";
import NearbyFoodMap from "../components/NearbyFoodMap";

const API_URL = process.env.REACT_APP_API_URL;
const accuracyData = [
  { label: "Ünlü simgesel yapılar", percentage: 90 },
  { label: "Büyük şehirler", percentage: 80 },
  { label: "Genel konumlar", percentage: 75 },
];

const CircularProgressBar = ({ percentage, label }) => {
  const radius = 50;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className="flex flex-col items-center space-y-2 text-white">
      <svg className="w-24 h-24" viewBox="0 0 120 120">
        <circle
          className="text-gray-500 stroke-current stroke-1"
          fill="transparent"
          r={radius}
          cx="60"
          cy="60"
        />
        <circle
          className="text-yellow-500 stroke-current stroke-10"
          fill="transparent"
          r={radius}
          cx="60"
          cy="60"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform="rotate(-90 60 60)"
        />
        <text
          x="60"
          y="65"
          textAnchor="middle"
          className="text-white font-semibold text-lg"
          fill="white"
        >
          {percentage}%
        </text>
      </svg>
      <span className="text-gray-300 text-sm">{label}</span>
    </div>
  );
};

const AccordionItem = memo(({ title, icon, children, isOpen, toggle }) => {
  return (
    <div className="border-b border-gray-600">
      <button
        className="flex items-center justify-between w-full p-4 text-left text-white font-semibold hover:bg-gray-600 transition duration-200"
        onClick={toggle}
      >
        <div className="flex items-center space-x-2">
          {icon}
          <span>{title}</span>
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5" />
        ) : (
          <ChevronDown className="w-5 h-5" />
        )}
      </button>
      <div
        className={`accordion-content ${isOpen ? "open" : ""}`}
        style={{
          maxHeight: isOpen ? "1000px" : "0",
          overflow: "hidden",
          transition: "max-height 0.3s ease-out",
        }}
      >
        <div className="p-4 text-gray-300">{children}</div>
      </div>
    </div>
  );
});

const accordionReducer = (state, action) => {
  switch (action.type) {
    case "TOGGLE":
      return { ...state, [action.section]: !state[action.section] };
    default:
      return state;
  }
};

function UploadForm() {
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [status, setStatus] = useState("idle");
  const [responseData, setResponseData] = useState(null);
  const [note, setNote] = useState("");
  const [noteSaved, setNoteSaved] = useState(false);
  const [refreshComments, setRefreshComments] = useState(false);
  const [isTipsModalOpen, setIsTipsModalOpen] = useState(false);
  const [reported, setReported] = useState(false);
  const [reportInfo, setReportInfo] = useState(false);
  const [rating, setRating] = useState(null);
  const [isGuest, setIsGuest] = useState(false);
  const [isAccuracyModalOpen, setIsAccuracyModalOpen] = useState(false);
  const [isNearbyPlacesModalOpen, setIsNearbyPlacesModalOpen] = useState(false);
  const [isFoodMapModalOpen, setIsFoodMapModalOpen] = useState(false);
  const [isLowConfidenceModalOpen, setIsLowConfidenceModalOpen] = useState(false);
  const [tokenModal, setTokenModal] = useState({ isOpen: false, message: '', requiredAmount: 0 });
  const [aiComment, setAiComment] = useState("");
  const [aiCommentStatus, setAiCommentStatus] = useState("idle");

  // --- TOKEN KONTROLÜ İÇİN STATE'LER ---
  const [userTokens, setUserTokens] = useState(null);
  const [isLoadingTokens, setIsLoadingTokens] = useState(true);

  const [accordionStates, dispatch] = useReducer(accordionReducer, {
    sesliAnlatim: false,
    tarihce: false,
    fotografRehberi: false,
    yakinindakiYerler: false,
    yakinindakiSosyalYerler: false,
    ilgincBilgiler: false,
    ilgiliLinkler: false,
    ilginiziCekebilir: false,
  });

  // --- TOKEN BİLGİSİNİ ÇEKME EFFECT'İ ---
  useEffect(() => {
    const fetchTokens = async () => {
      const token = localStorage.getItem("token");

      if (!token) {
        setIsLoadingTokens(false);
        setUserTokens(0);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/api/wallet/balance`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setUserTokens(response.data.balance);
      } catch (error) {
        console.error("Bakiye bilgisi çekilemedi:", error);
        setUserTokens(0);
      } finally {
        setIsLoadingTokens(false);
      }
    };

    fetchTokens();
  }, []);

    const [featureCosts, setFeatureCosts] = useState({
    analyze_quick: null,
    generate_ai_comment: null
  });


  // Analiz için 100 token gerekiyor
  const ANALYSIS_COST = featureCosts.analyze_quick;
  const COMMENT_COST = featureCosts.generate_ai_comment;

  // Fiyatlar DB'den yüklenene kadar null olacağı için ekstra null kontrolü ekledik
  const hasEnoughTokensForAnalysis = userTokens !== null && ANALYSIS_COST !== null && userTokens >= ANALYSIS_COST;
  const hasEnoughTokensForComment = userTokens !== null && COMMENT_COST !== null && userTokens >= COMMENT_COST;


  // --- VERİ ÇEKME EFFECT'İ (GÜNCELLENDİ) ---
  useEffect(() => {
    const fetchData = async () => {
      // 1. Önce maliyetleri (fiyat listesini) backend'den çek
      try {
        const costsResponse = await axios.get(`${API_URL}/api/features/costs`);
        setFeatureCosts({
          analyze_quick: costsResponse.data.analyze_quick || 100, // DB'den gelmezse fallback
          generate_ai_comment: costsResponse.data.generate_ai_comment || 20
        });
      } catch (error) {
        console.error("Maliyet bilgileri çekilemedi, varsayılan değerler kullanılıyor.", error);
        setFeatureCosts({ analyze_quick: 100, generate_ai_comment: 20 });
      }

      // 2. Kullanıcının güncel token bakiyesini çek
      const token = localStorage.getItem("token");
      if (!token) {
        setIsLoadingTokens(false);
        setUserTokens(0);
        return;
      }

      try {
        const balanceResponse = await axios.get(`${API_URL}/api/wallet/balance`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUserTokens(balanceResponse.data.balance);
      } catch (error) {
        console.error("Bakiye bilgisi çekilemedi:", error);
        setUserTokens(0);
      } finally {
        setIsLoadingTokens(false);
      }
    };

    fetchData();
  }, []);

const handleGenerateAIComment = async (e) => {
    e.preventDefault();

    // 1. GÜVENLİK KONTROLLERİ
    // İşlem devam ediyorsa, bakiye/maliyet yüklenmemişse veya veri yoksa durdur.
    if (!responseData || !responseData.id || aiCommentStatus === "loading" || COMMENT_COST === null) return;

    // 2. TOKEN BAKİYE KONTROLÜ (Dinamik Modal ile)
    if (!hasEnoughTokensForComment) {
        setTokenModal({ 
            isOpen: true, 
            message: `Fotoğraf yorumu oluşturmak için yeterli tokeniniz bulunmuyor. Bu işlem için ${COMMENT_COST} token gereklidir.`, 
            requiredAmount: COMMENT_COST 
        });
        return;
    }

    setAiCommentStatus("loading");
    setAiComment("Yapay zeka yorumu oluşturuluyor... Lütfen bekleyin.");
    
    // 3. OPTİMİSTİK GÜNCELLEME
    // İşlem başlamadan önce arayüzde tokeni düşür, hata olursa catch bloğunda iade edeceğiz.
    const previousTokens = userTokens;
    setUserTokens(prev => (prev !== null ? prev - COMMENT_COST : prev));

    try {
        const token = localStorage.getItem("token");
        if (!token) {
            setUserTokens(previousTokens); // Token yoksa iade et
            setAiCommentStatus("error");
            setTokenModal({ 
                isOpen: true, 
                message: 'Yorum oluşturmak için giriş yapmalısınız.', 
                requiredAmount: 0 
            });
            navigate("/login");
            return;
        }

        const response = await axios.get(
            `${API_URL}/generate-ai-comment/${responseData.id}`,
            {
                headers: { Authorization: `Bearer ${token}` },
            }
        );

        if (response.status !== 200 || !response.data?.ai_comment) {
            throw new Error(response.data?.detail || "Gemini yorumu alınamadı.");
        }

        setAiComment(response.data.ai_comment);
        setAiCommentStatus("success");

    } catch (error) {
        console.error("Gemini Yorumu Oluşturma Hatası:", error);
        
        // 4. HATA YÖNETİMİ VE İADE (Rollback)
        setUserTokens(previousTokens); // Düşülen tokeni geri yükle
        setAiCommentStatus("error");

        if (error.response?.status === 402) {
            setAiComment("Yetersiz bakiye.");
            setTokenModal({ 
                isOpen: true, 
                message: 'Sunucuda yetersiz bakiye tespit edildi. Lütfen cüzdanınıza yükleme yapın.', 
                requiredAmount: COMMENT_COST 
            });
        } else {
            const errorMsg = error.response?.data?.detail || error.message;
            setAiComment(`Hata: ${errorMsg}`);
            // Kritik olmayan hatalarda alert yerine küçük bir uyarı veya modal kullanabilirsin
            alert("İşlem sırasında bir sorun oluştu, tokeniniz iade edildi.");
        }
    }
};

  const renderBulletedList = (text) => {
    if (!text) {
      return <p className="text-gray-400 italic">Bilgi bulunamadı.</p>;
    }
    const lines = text
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
    const elements = [];
    let currentList = [];

    lines.forEach((line, index) => {
      if (line.startsWith("* **") && line.endsWith("**:")) {
        if (currentList.length > 0) {
          elements.push(
            <ul
              key={`ul-${index}`}
              className="list-disc list-inside text-gray-300"
            >
              {currentList}
            </ul>
          );
          currentList = [];
        }
        elements.push(
          <h4
            key={`h4-${index}`}
            className="text-lg font-semibold text-blue-300 mt-4"
          >
            {line.substring(3, line.length - 3)}
          </h4>
        );
      } else if (line.startsWith("* ")) {
        const content = line.substring(2);
        const formattedContent = content.replace(
          /\*\*(.*?)\*\*/g,
          "<strong>$1</strong>"
        );
        currentList.push(
          <li
            key={`li-${index}`}
            dangerouslySetInnerHTML={{ __html: formattedContent }}
          />
        );
      } else if (
        line.startsWith("**") &&
        line.endsWith("**") &&
        !line.includes(":")
      ) {
        const content = line.substring(2, line.length - 2);
        currentList.push(
          <li key={`li-${index}`}>
            <strong>{content}</strong>
          </li>
        );
      } else {
        if (currentList.length > 0) {
          elements.push(
            <ul
              key={`ul-${index}`}
              className="list-disc list-inside text-gray-300"
            >
              {currentList}
            </ul>
          );
          currentList = [];
        }
        elements.push(
          <p key={`p-${index}`} className="text-gray-300">
            {line}
          </p>
        );
      }
    });

    if (currentList.length > 0) {
      elements.push(
        <ul key="ul-final" className="list-disc list-inside text-gray-300">
          {currentList}
        </ul>
      );
    }
    return <div className="space-y-2">{elements}</div>;
  };

  const renderRelatedLinks = (linksString) => {
    if (!linksString) {
      return <p className="text-gray-400 italic">İlgili link bulunamadı.</p>;
    }
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const linksArray = linksString.match(urlRegex);
    if (linksArray && linksArray.length > 0) {
      return (
        <ul className="list-disc list-inside text-gray-300">
          {linksArray.map((link, index) => (
            <li key={index}>
              <a
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-300 hover:underline"
              >
                {link}
              </a>
            </li>
          ))}
        </ul>
      );
    } else {
      return <p className="text-gray-400 italic">İlgili link bulunamadı.</p>;
    }
  };

  const renderNumberedList = (text) => {
    if (!text) {
      return <p className="text-gray-400 italic">Bilgi bulunamadı.</p>;
    }
    const regex = /(\d+\.\s.*?)(?=\s*\d+\.|\s*$)/g;
    const matches = [...text.matchAll(regex)];
    if (matches.length > 0) {
      return (
        <ol className="list-decimal list-inside text-gray-300">
          {matches.map((match, index) => (
            <li key={index}>{match?.[1]?.trim()}</li>
          ))}
        </ol>
      );
    } else {
      return <p className="text-gray-300">{text}</p>;
    }
  };

  const accordionContent = useMemo(
    () => ({
      sesliAnlatim: responseData?.sesliAnlatim || (
        <span className="text-gray-400 italic">Bilgi bulunamadı.</span>
      ),
      tarihce: responseData?.tarihce || (
        <span className="text-gray-400 italic">Bilgi bulunamadı.</span>
      ),
      fotografRehberi: renderBulletedList(responseData?.fotograf_rehberi) || (
        <span className="text-gray-400 italic">Bilgi bulunamadı.</span>
      ),
      yakinindakiYerler: renderNumberedList(responseData?.mekan) || (
        <span className="text-gray-400 italic">Bilgi bulunamadı.</span>
      ),
      ilgincBilgiler: responseData?.ilginc_bilgi || (
        <span className="text-gray-400 italic">Bilgi bulunamadı.</span>
      ),
      ilgiliLinkler: renderRelatedLinks(responseData?.ilgili_linkler) || (
        <span className="text-gray-400 italic">İlgili link bulunamadı.</span>
      ),
      googlePuan: responseData?.google || (
        <span className="text-gray-400 italic">Bilgi bulunamadı.</span>
      ),
      googleYorum: responseData?.googleYorum || (
        <span className="text-gray-400 italic">Aranıyor</span>
      ),
    }),
    [responseData]
  );

  const toggleAccordion = (section) => {
    dispatch({ type: "TOGGLE", section });
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith("image/")) {
      setSelectedImage(file);
      setPreviewUrl(URL.createObjectURL(file));
      setStatus("idle");
      setResponseData(null);
      setNote("");
      setNoteSaved(false);
      setRating(0);
      setAiComment("");
      setAiCommentStatus("idle");
      setIsLowConfidenceModalOpen(false);
    } else if (file) {
      setStatus("error");
      setResponseData(null);
      setNote("");
      setNoteSaved(false);
      setRating(0);
      alert("Lütfen bir görsel dosyası seçin.");
    }
  };

  // --- ANALİZ VE TOKEN DÜŞME FONKSİYONU ---
// --- ANALİZ (DİNAMİK TOKEN MALİYETLİ) ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedImage) {
      setStatus("error");
      alert("Lütfen önce bir fotoğraf seçin.");
      return;
    }

    // Fiyatlar henüz backend'den gelmediyse işlemi beklet
    if (ANALYSIS_COST === null) return;

    // 1. Dinamik Bakiye Kontrolü (Token Modal Kullanımı)
    if (!hasEnoughTokensForAnalysis) {
      setTokenModal({ 
        isOpen: true, 
        message: 'Mekân analizi için yeterli tokeniniz bulunmuyor.', 
        requiredAmount: ANALYSIS_COST 
      });
      return;
    }

    setStatus("loading");
    setResponseData(null);
    setNote("");
    setNoteSaved(false);
    setRating(0);
    setAiComment("");
    setAiCommentStatus("idle");
    setIsLowConfidenceModalOpen(false);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setTokenModal({ 
          isOpen: true, 
          message: 'Analiz yapmak için giriş yapmalısınız.', 
          requiredAmount: 0 
        });
        navigate("/login");
        return;
      }

      const formData = new FormData();
      formData.append("file", selectedImage);

      // 2. Başlamadan önce arayüzde tokeni optimistik olarak düş. 
      // Hata olursa geri yükleyeceğiz. (Dinamik Fiyat Kullanımı)
      const previousTokens = userTokens;
      setUserTokens((prev) => prev !== null ? prev - ANALYSIS_COST : prev);

      try {
        const quickResponse = await axios.post(
          `${API_URL}/analyze-quick`,
          formData,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const quickData = quickResponse.data;
        setResponseData({
          id: quickData.id,
          location: quickData.location,
          confidence: quickData.confidence,
          latitude: quickData.latitude,
          longitude: quickData.longitude,
          tarihce: "Detaylı bilgiler yükleniyor...",
          fotograf_rehberi: "Detaylı bilgiler yükleniyor...",
          ilginc_bilgi: "Detaylı bilgiler yükleniyor...",
          loading_details: true,
        });
        setStatus("success");

        if (quickData.confidence < 0.20) {
          setIsLowConfidenceModalOpen(true);
        }

        if (quickData.confidence < 0.6) setReported(true);
        else setReported(false);

        const guess = localStorage.getItem("is_guest");
        setIsGuest(guess === "true");

        // Detaylı analiz isteği
        try {
          const detailsResponse = await axios.get(
            `${API_URL}/analyze-details/${quickData.id}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );

          setResponseData((prevData) => ({
            ...prevData,
            ...detailsResponse.data,
            loading_details: false,
          }));
        } catch (detailsError) {
          console.error("Detaylı bilgiler yüklenirken hata:", detailsError);
          setResponseData((prevData) => ({
            ...prevData,
            tarihce: "Detaylı bilgiler yüklenemedi.",
            fotograf_rehberi: "Detaylı bilgiler yüklenemedi.",
            ilginc_bilgi: "Detaylı bilgiler yüklenemedi.",
            loading_details: false,
          }));
        }
      } catch (error) {
        // 3. Sunucudan hata dönerse düşülen tokeni arayüze geri ekle
        setUserTokens(previousTokens);

        if (error.response?.status === 402) {
          setStatus("error");
          // Eski alert yerine yeni TokenModal
          setTokenModal({ 
            isOpen: true, 
            message: 'Sunucuda yetersiz bakiye tespit edildi. Lütfen cüzdanınıza yükleme yapın.', 
            requiredAmount: ANALYSIS_COST 
          });
        } else if (error.response?.status === 401) {
          alert("Oturumunuzun süresi doldu veya yetkiniz yok. Lütfen tekrar giriş yapın.");
          localStorage.removeItem("token");
          navigate("/login");
        } else {
          setStatus("error");
          alert(
            `Analiz sırasında bir sorun oluştu, tokeniniz iade edildi: ${
              error.response?.data?.detail || error.message
            }`
          );
        }
      }
    } catch (error) {
      console.error("Yükleme veya hazırlık hatası:", error);
      setStatus("error");
    }
  };

  const handleReset = () => {
    setSelectedImage(null);
    setPreviewUrl(null);
    setStatus("idle");
    setResponseData(null);
    setNote("");
    setNoteSaved(false);
    setIsTipsModalOpen(false);
    setRating(0);
    setIsLowConfidenceModalOpen(false);
  };

  const handleShare = () => {
    const url = `https://mekanname.com/`;
    navigator.clipboard.writeText(url);
    alert("Bağlantı panoya kopyalandı!");
  };

  const handleReport = async (event, location) => {
    event.preventDefault();
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("Bildirim yapmak için giriş yapmalısınız.");
        return;
      }
      const response = await axios.post(
        `${API_URL}/report-prediction`,
        { location: location },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReportInfo(true);
      setReported(false);
      console.log(response.data.message);
    } catch (error) {
      console.error("Bildirim gönderilirken hata oluştu:", error);
      alert("Bildirim gönderilirken bir hata oluştu. Lütfen tekrar deneyin.");
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!responseData || !responseData.id) {
      alert("Not eklemek için önce bir tahmin yapmalısınız.");
      return;
    }
    if (note.trim() === "") {
      alert("Lütfen notunuzu girin.");
      return;
    }
    const token = localStorage.getItem("token");
    if (!token) {
      alert("Not eklemek için giriş yapmalısınız.");
      return;
    }
    try {
      const response = await axios.post(
        `${API_URL}/add_note`,
        { prediction_id: responseData.id, note: note },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (response.status !== 200) {
        throw new Error(
          response.data?.detail || "Not kaydedilirken sunucu hatası."
        );
      }
      console.log(response.data.message);
      setNoteSaved(true);
      setTimeout(() => setNoteSaved(false), 3000);
    } catch (error) {
      console.error("Not kaydetme hatası:", error);
      alert(
        `Not kaydedilirken bir hata oluştu: ${
          error.response?.data?.detail || error.message
        }`
      );
      setNoteSaved(false);
    }
  };

  const handleRating = async (newRating) => {
    setRating(newRating);
    console.log(
      `Tahmine puan verildi (ID: ${
        responseData?.id || "Yok"
      }, Puan: ${newRating})`
    );
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("Puanlamak için giriş yapmalısınız.");
        return;
      }
      await axios.post(
        `${API_URL}/rate_prediction`,
        { prediction_id: responseData.id, rating: newRating },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("Puanınız kaydedildi!");
    } catch (error) {
      console.error("Puan kaydetme hatası:", error);
      alert(
        "Puan kaydedilirken bir hata oluştu: " +
          (error.response?.data?.detail || error.message)
      );
    }
  };

  const displayAiComment = useMemo(() => {
    if (aiCommentStatus === "idle" && !aiComment) {
      return "Fotoğrafınız için yapay zeka yorumunu oluşturmak üzere aşağıdaki butona tıklayın.";
    }
    if (aiCommentStatus === "loading") {
      return (
        <span className="flex items-center justify-center">
          <Loader2 className="animate-spin w-4 h-4 mr-2" />
          Yorum oluşturuluyor...
        </span>
      );
    }
    if (aiCommentStatus === "error") {
      return `Hata: ${aiComment.substring(0, 50)}... Tekrar Deneyin.`;
    }
    return aiComment;
  }, [aiComment, aiCommentStatus]);

  return (
    <div className="bg-gradient-to-br from-gray-900 to-gray-800 min-h-screen py-6 sm:py-12 text-center">
      <h1 className="text-5xl md:text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-indigo-500 mb-12 drop-shadow-md">
        MEKÂNNAME AI
      </h1>
      <div className="relative py-3 max-w-5xl mx-auto">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-300 to-blue-600 shadow-lg transform -skew-y-6 sm:skew-y-0 sm:-rotate-6 sm:rounded-3xl"></div>
        <div className="relative bg-gray-800 shadow-lg sm:rounded-3xl px-4 py-6 sm:p-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Sol sütun: Doğruluk ve Performans */}
            <div className="lg:col-span-1 order-2 lg:order-1">
              <div
                className={`transition-all duration-500 ease-in-out mx-auto rounded-xl shadow-2xl overflow-hidden ${
                  selectedImage
                    ? "p-2 bg-gray-800/50 mb-2 max-w-md border border-gray-700/50"
                    : "p-6 bg-gray-700 space-y-5 max-w-lg"
                }`}
              >
                <div className="text-center">
                  {!selectedImage && (
                    <div className="mb-2 inline-flex items-center px-3 py-1 bg-blue-600/20 rounded-full text-xs font-medium text-blue-300 transition-opacity duration-300">
                      <span
                        role="img"
                        aria-label="location"
                        className="mr-1"
                      >
                        📍
                      </span>
                      MekannameAI: **Ankara**'da Hizmet Veriyor
                    </div>
                  )}

                  <h1
                    className={`font-extrabold text-gray-100 flex items-center justify-center space-x-3 transition-all duration-500 ${
                      selectedImage ? "text-xl mb-0" : "text-3xl mb-2"
                    }`}
                  >
                    <i
                      className={`fas fa-camera-retro transition-all duration-500 ${
                        selectedImage ? "text-xl text-blue-400" : "text-3xl"
                      }`}
                    ></i>
                    <span>Mekanname Gezgini</span>
                  </h1>

                  {!selectedImage && (
                    <p className="text-gray-400 text-lg font-light max-w-sm mx-auto mt-2 transition-opacity duration-500">
                      Bir tarihi mekânın fotoğrafını yükle, neresi olduğunu ve
                      ilginç tarihini anında öğren!
                    </p>
                  )}
                </div>

                {!selectedImage && (
                  <div className="flex flex-col sm:flex-row justify-center gap-2 transition-opacity duration-500">
                    <button
                      type="button"
                      className="text-gray-300 bg-gray-800/50 hover:bg-gray-800 text-sm py-2 px-4 rounded-lg transition-all duration-300 transform focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                      onClick={() => setIsTipsModalOpen(true)}
                    >
                      💡 Daha İyi Sonuçlar İçin
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsAccuracyModalOpen(true)}
                      className="text-gray-300 bg-gray-800/50 hover:bg-gray-800 text-sm py-2 px-4 rounded-lg transition-all duration-300 transform focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                    >
                      📈 Doğruluk ve Performans
                    </button>
                  </div>
                )}
              </div>

              <form onSubmit={handleSubmit} className="space-y-4 mt-2">
                {!selectedImage && (
                  <div className="bg-gray-700 rounded-md p-4 flex flex-col items-center justify-center border border-gray-600">
                    <label
                      htmlFor="image-upload"
                      className="flex flex-col items-center justify-center cursor-pointer text-gray-300 hover:text-blue-300 transition duration-300"
                    >
                      <Upload className="w-10 h-10 mb-2" />
                      <p className="text-sm">Fotoğraf Seç</p>
                    </label>
                    <input
                      type="file"
                      id="image-upload"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                  </div>
                )}

                {previewUrl && (
                  <div className="relative">
                    <img
                      src={previewUrl}
                      alt="Seçilen Mekan"
                      className="w-full rounded-md shadow-md"
                    />
                    {status === "loading" && <MagnifyingGlassAnimation />}
                    <button
                      type="button"
                      onClick={handleReset}
                      className="absolute top-2 right-2 bg-gray-600 text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-gray-500 focus:outline-none"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                )}

                {status === "error" && (
                  <div
                    className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
                    role="alert"
                  >
                    <strong className="font-bold">Hata!</strong>
                    <span className="block sm:inline">
                      {" "}
                      Lütfen geçerli bir görsel yükleyin veya tekrar deneyin.
                    </span>
                    <span className="absolute top-0 bottom-0 right-0 px-4 py-3">
                      <XCircle
                        className="h-6 w-6 fill-current"
                        aria-hidden="true"
                      />
                    </span>
                  </div>
                )}

                {status === "loading" && (
                  <div>
                    <LoadingBar loading={status} />
                    <div
                      className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 mt-2 rounded relative"
                      role="alert"
                    >
                      <Loader2 className="animate-spin w-6 h-6 mr-2 inline-block" />
                      <span className="block sm:inline">
                        {" "}
                        Analiz ediliyor... Lütfen bekleyin.{" "}
                      </span>
                    </div>
                  </div>
                )}

                {/* --- REVİZE EDİLEN KISIM: Analiz Butonu ve Token Kontrolü --- */}
                {status !== "success" && (
                  <div className="text-center flex flex-col items-center gap-3">
                    <button
                      type="submit"
                      disabled={status === "loading" || !selectedImage || !hasEnoughTokensForAnalysis || isLoadingTokens}
                      className={`bg-blue-500 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-full focus:outline-none focus:shadow-outline transition-all ${
                        status === "loading" || !selectedImage || !hasEnoughTokensForAnalysis || isLoadingTokens
                          ? "opacity-50 cursor-not-allowed grayscale-[30%]"
                          : ""
                      }`}
                    >
                      {/* {status === "loading" ? (
                          <>
                            <Loader2 className="animate-spin w-5 h-5 mr-2 inline-block" /> Yükleniyor...
                          </>
                        ) : isLoadingTokens ? (
                          "Cüzdan Kontrol Ediliyor..."
                        ) : (
                          "Analiz Et "
                      )}*/}
                      Analiz Et ({ANALYSIS_COST} Token)
                    </button>
                    
                    {/* Yetersiz Bakiye Uyarı Mesajı */}
                    {!isLoadingTokens && !hasEnoughTokensForAnalysis && ANALYSIS_COST !== null && (
                      <div className="text-red-400 text-sm font-medium bg-red-900/30 px-4 py-2 rounded-lg border border-red-500/30 flex items-center gap-2 animate-in fade-in slide-in-from-bottom-2">
                        <AlertCircle size={16} />
                        <span>
                          Bu işlem için {ANALYSIS_COST} token gerekli. (Mevcut: {userTokens || 0})
                        </span>
                      </div>
                    )}
                  </div>
                )}
                {/* -------------------------------------------------------- */}

                {status === "success" && responseData && (
                  <div className="mb-8 flex gap-1 items-center justify-center text-center">
                    <Gallery placeName={responseData.location} />
                    <div className="">
                      <button
                        className="px-6 py-3 bg-indigo-600 text-white rounded-full font-semibold shadow-lg hover:bg-indigo-700 transition-colors duration-300 transform hover:-translate-y-1"
                        onClick={() => setIsNearbyPlacesModalOpen(true)}
                        type="button"
                      >
                        <div className="flex items-center space-x-2">
                          <span className="text-white">🏛️</span>
                          <span>Çevresindeki Mekânlar</span>
                        </div>
                      </button>
                    </div>
                  </div>
                )}
              </form>

              {status === "success" && responseData && (
                <div>
                  <div className="bg-gray-700 rounded-lg p-5 mt-3 mb-3 border border-gray-600 shadow-xl">
                    <div className="mb-4 text-white">
                      <h2 className="text-xl text-white font-bold flex items-center space-x-2">
                        <CheckCircle className="text-green-400 w-6 h-6 flex-shrink-0" />
                        <span>Analiz Başarılı!</span>
                      </h2>
                      <p className="mt-2 text-gray-300 ml-8">
                        <span className="text-sm font-semibold text-yellow-300">
                          %{(responseData.confidence * 100).toFixed(1)} Güvenle:
                        </span>
                        <br />
                        <a
                          href={`http://google.com/maps/search/${encodeURIComponent(
                            responseData.location
                          )}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-2xl font-extrabold text-blue-300 hover:text-blue-200 transition-colors"
                        >
                          📍 {responseData.location}
                        </a>
                        <a href={accordionContent.googleYorum} target="_blank">
                          {" "}
                          ⭐{accordionContent.googlePuan}{" "}
                        </a>
                      </p>
                    </div>

                    <div className="flex flex-wrap text-center items-center justify-center gap-3 mt-4 pt-4 border-t border-gray-600/50">
                      <button
                        type="button"
                        onClick={handleShare}
                        className="bg-blue-400 hover:bg-blue-500 text-gray-900 font-semibold py-2 px-4 rounded-full text-sm transition-all focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        🔗 Tahmini Paylaş
                      </button>

                      {reported && (
                        <button
                          onClick={(event) =>
                            handleReport(event, responseData.location)
                          }
                          className="flex items-center text-sm text-red-400 hover:text-red-300 font-medium transition-colors p-2 rounded-md hover:bg-gray-800/50"
                          title="Tahminin hatalı olduğunu düşünüyorsanız bildirin"
                        >
                          <span className="mr-1">⚠️</span>
                          Tahmin Hatalı mı? Bildir!
                        </button>
                      )}
                    </div>

                    {reportInfo && (
                      <div className="mt-3 p-2 bg-green-800/50 border border-green-700 rounded-md text-xs text-center">
                        <p className="text-green-300">
                          ✅ Bildiriminiz alındı. Geri bildiriminiz için
                          teşekkürler!
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="bg-gradient-to-br from-gray-800 via-gray-900 to-black rounded-2xl p-6 border border-blue-500/60 shadow-2xl shadow-blue-500/20 space-y-4">
                    <div className="flex justify-between items-center">
                        <h3 className="text-2xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300 flex items-center space-x-2">
                          <Sparkles className="w-6 h-6" />
                          <span>MekânnameAI Fotoğraf Yorumu</span>
                        </h3>
                    </div>

                    <p
                      className={`leading-relaxed border-l-4 pl-4 rounded-md p-2 min-h-[50px] flex items-center ${
                        aiCommentStatus === "loading"
                          ? "text-blue-300 bg-blue-900/40 border-blue-400/80 justify-center"
                          : aiCommentStatus === "error"
                          ? "text-red-300 bg-red-900/40 border-red-400/80"
                          : "text-gray-100 italic bg-gray-900/40 border-blue-400/80"
                      }`}
                    >
                      {displayAiComment}
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-3 border-t border-gray-700/70">
                    <button
                      onClick={handleGenerateAIComment}
                      // Yükleme sırasında, fiyatlar henüz gelmemişken veya bakiye yetersizken disabled olsun
                      disabled={aiCommentStatus === "loading" || COMMENT_COST === null || !hasEnoughTokensForComment}
                      className={`flex items-center justify-center space-x-1 font-semibold py-2 px-4 rounded-full text-sm transition-all duration-300 w-full sm:w-auto ${
                        aiCommentStatus === "loading" || COMMENT_COST === null || !hasEnoughTokensForComment
                          ? "bg-blue-800/50 text-blue-300 opacity-60 cursor-not-allowed border border-blue-800/50 grayscale-[50%]"
                          : "bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-900/50 active:scale-95"
                      }`}
                    >
                      {aiCommentStatus === "loading" ? (
                        <Loader2 className="animate-spin w-4 h-4 mr-1" />
                      ) : aiCommentStatus === "success" ? (
                        <RefreshCw className="w-4 h-4 mr-1" />
                      ) : (
                        <Sparkles className="w-4 h-4 mr-1" />
                      )}
                      <span>
                        {aiCommentStatus === "loading"
                          ? "Oluşturuluyor..."
                          : COMMENT_COST !== null 
                            ? `Yorum Oluştur (${COMMENT_COST} Token)` 
                            : "Yükleniyor..."}
                      </span>
                    </button>

                      {aiCommentStatus === "success" && (
                        <div className="flex gap-2">
                          <button className="flex items-center space-x-1 text-sm px-3 py-1.5 rounded-lg bg-slate-800 text-gray-300 hover:text-green-400 hover:bg-slate-700 transition-colors border border-slate-700">
                            <span>👍</span>
                            <span>Beğen</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="lg:col-span-1 order-2 -mt-8">
              {status === "success" && responseData && (
                <div className="mt-8 space-y-6">
                  <AudioNarrationCard
                    placeName={responseData.location}
                    narrationStyle={responseData.anlatim_tarzi}
                    userTokens={userTokens} // Ana state'den gelen bakiye
                    onTokenSpent={(amount) => setUserTokens(prev => prev - amount)} // Bakiyeyi düşüren / iade eden fonksiyon
                    onShowTokenModal={(msg, cost) => setTokenModal({ isOpen: true, message: msg, requiredAmount: cost })} // Yetersiz bakiye modalını tetikler
                  />
                  <div className="bg-gray-700 rounded-md p-6 border border-gray-600">
                    <h3 className="text-lg text-blue-300 font-semibold mb-4">
                      Detaylı Bilgiler
                    </h3>
                    <div className="space-y-2">
                      <AccordionItem
                        title="Sesli Anlatım"
                        icon={<span className="text-white">🔊</span>}
                        isOpen={accordionStates.sesliAnlatim}
                        toggle={(e) => {
                          e.preventDefault();
                          toggleAccordion("sesliAnlatim");
                        }}
                      >
                        <VoiceNarration
                          placeName={responseData.location}
                          style={responseData.anlatim_tarzi}
                        />
                      </AccordionItem>
                      <AccordionItem
                        title="Tarihçe"
                        icon={<span className="text-white">📜</span>}
                        isOpen={accordionStates.tarihce}
                        toggle={(e) => {
                          e.preventDefault();
                          toggleAccordion("tarihce");
                        }}
                      >
                        {accordionContent.tarihce}
                        <br />
                        <br />
                        <hr />
                        <br />
                        {accordionContent.ilgiliLinkler}
                      </AccordionItem>
                      <AccordionItem
                        title="Fotoğraf Rehberi"
                        icon={<span className="text-white">🧭</span>}
                        isOpen={accordionStates.fotografRehberi}
                        toggle={(e) => {
                          e.preventDefault();
                          toggleAccordion("fotografRehberi");
                        }}
                      >
                        {accordionContent.fotografRehberi}
                      </AccordionItem>
                      <AccordionItem
                        title="İlginç Bilgiler"
                        icon={<span className="text-white">📚</span>}
                        isOpen={accordionStates.ilgincBilgiler}
                        toggle={(e) => {
                          e.preventDefault();
                          toggleAccordion("ilgincBilgiler");
                        }}
                      >
                        {accordionContent.ilgincBilgiler}
                      </AccordionItem>

                      <AccordionItem
                        title="Lezzet Durağı ☕"
                        icon={<Sparkles className="text-white w-5 h-5" />}
                        isOpen={accordionStates.ilginiziCekebilir}
                        toggle={(e) => {
                          e.preventDefault();
                          toggleAccordion("ilginiziCekebilir");
                        }}
                      >
                        <div className="grid gap-4 pt-2">
                          <div className="bg-gray-800/60 p-4 rounded-xl border border-gray-600/50 hover:bg-gray-800 transition-colors flex items-start space-x-4">
                            <div className="bg-orange-900/40 p-2 rounded-lg text-orange-400 shrink-0">
                              <Utensils size={20} />
                            </div>
                            <div className="flex-1">
                              <h4 className="text-orange-300 font-bold text-sm mb-1">
                                
                              </h4>
                              <p className="text-gray-300 text-sm leading-snug mb-3">
                                Geziden sonra acıktın mı? Veya kahve molası mı vermek istiyorsun? Yakınlardaki en iyi
                                yöresel lezzetleri ve kafeleri haritada gör.
                              </p>
                              <button
                                onClick={() => setIsFoodMapModalOpen(true)}
                                className="w-full flex items-center justify-center space-x-2 py-2 px-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-xs font-bold transition-all shadow-md active:scale-95"
                              >
                                <Map size={14} />
                                <span>Haritada Göster</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </AccordionItem>

                      <p className="bg-green-900 bg-opacity-50 text-green-300 py-2 px-3 rounded-md text-sm">
                        ✅ Bu mekânı ziyaret ettin! Profiline özel rozet
                        eklendi.
                      </p>
                    </div>
                  </div>

                  <div className="rounded-md ">
                    <AddComment
                      disabled={isGuest}
                      responseData={responseData}
                      onSuccess={() => setRefreshComments((prev) => !prev)}
                    />
                  </div>

                  <div className="bg-gray-700 rounded-md border border-gray-600">
                    <CommentList
                      disabled={isGuest}
                      placeName={responseData.location}
                      refreshTrigger={refreshComments}
                    />
                  </div>
                  {isGuest && (
                    <p className="text-sm text-red-500 mt-2">
                      Misafir kullanıcı not veya yorum ekleyemez.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {isTipsModalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
              <div className="relative bg-gray-800 rounded-md shadow-lg p-6 max-w-lg w-full">
                <button
                  className="absolute top-2 right-2 text-gray-300 hover:text-white focus:outline-none"
                  onClick={() => setIsTipsModalOpen(false)}
                >
                  <X className="w-6 h-6" />
                </button>
                <h3 className="text-xl text-blue-300 font-semibold mb-4">
                  Fotoğraf Yükleme İpuçları
                </h3>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start space-x-3">
                    <div className="text-green-400">
                      <CheckCircle className="w-5 h-5" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-white">
                        Net ve Odaklı Fotoğraflar
                      </h5>
                      <p className="text-sm">
                        Yapının ana hatlarının belirgin olduğu, bulanık olmayan
                        fotoğraflar en iyi sonuçları verir.
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="text-green-400">
                      <CheckCircle className="w-5 h-5" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-white">
                        Yapıyı Mümkünse Tam Çekin
                      </h5>
                      <p className="text-sm">
                        Mekânın tamamını veya büyük bir kısmını içeren
                        fotoğraflar, ayırt edici özellikleri yakalamamızı
                        sağlar.
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="text-green-400">
                      <CheckCircle className="w-5 h-5" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-white">
                        İyi Işık Koşulları
                      </h5>
                      <p className="text-sm">
                        Gündüz çekilmiş, gölgelerin çok sert olmadığı aydınlık
                        fotoğraflar doğruluğu artırır.
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="text-red-400">
                      <XCircle className="w-5 h-5" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-white">
                        Aşırı Yakın Detaylardan Kaçının
                      </h5>
                      <p className="text-sm">
                        Sadece bir pencere, kapı veya süslemenin fotoğrafı
                        yerine yapının genelini tercih edin.
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="text-red-400">
                      <XCircle className="w-5 h-5" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-white">
                        Engellenmiş Görüntüler
                      </h5>
                      <p className="text-sm">
                        Yapının önünü büyük ölçüde kapatan ağaç, insan veya araç
                        gibi engellerden kaçının.
                      </p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>

      {isLowConfidenceModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
            onClick={() => setIsLowConfidenceModalOpen(false)}
          ></div>

          <div className="relative w-full max-w-md bg-gray-900 border border-gray-700/50 rounded-3xl shadow-2xl overflow-hidden transform transition-all animate-in fade-in zoom-in duration-300">
            <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-amber-500/10 to-transparent pointer-events-none"></div>

            <div className="relative p-8 flex flex-col items-center text-center">
              <div className="relative mb-6 group">
                <div className="absolute inset-0 bg-amber-500 blur-2xl opacity-20 group-hover:opacity-30 transition-duration-500 rounded-full"></div>
                <div className="relative bg-gray-800 p-4 rounded-2xl border border-gray-700 shadow-inner ring-1 ring-white/5">
                  <AlertTriangle className="w-10 h-10 text-amber-500" />
                </div>
              </div>

              <h3 className="text-2xl font-bold text-white mb-2 tracking-tight">
                Tam Emin Olamadım 🤔
              </h3>
              <p className="text-gray-400 mb-8 text-base leading-relaxed">
                Görseli analiz ederken yapay zeka biraz zorlandı (%20'den düşük
                güven oranı). Yine de en güçlü tahminimi görmek ister misin?
              </p>

              <div className="w-full bg-gray-800/50 border border-gray-700/50 rounded-xl p-4 mb-8 flex items-center justify-center gap-3 relative overflow-hidden group">
                <div className="absolute inset-0 bg-blue-500/5 group-hover:bg-blue-500/10 transition-colors"></div>
                <MapPin className="w-5 h-5 text-blue-400 shrink-0 relative z-10" />
                <span className="text-lg font-semibold text-blue-100 relative z-10 break-words line-clamp-1">
                  {responseData?.location || "Bilinmeyen Mekan"}
                </span>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 w-full">
                <button
                  onClick={handleReset}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white font-medium transition-all duration-200 border border-gray-700 hover:border-gray-600 focus:ring-2 focus:ring-gray-600 focus:outline-none"
                >
                  <RefreshCw className="w-4 h-4" />
                  <span>Tekrar Dene</span>
                </button>

                <button
                  onClick={() => setIsLowConfidenceModalOpen(false)}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white font-semibold shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all duration-200 transform hover:-translate-y-0.5 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                >
                  <Eye className="w-4 h-4" />
                  <span>Yine de Göster</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isAccuracyModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsAccuracyModalOpen(false);
          }}
        >
          <div className="bg-gray-800 w-full max-w-xl rounded-lg p-6 border border-gray-600 shadow-lg">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl text-white font-bold">
                Doğruluk ve Performans
              </h2>
              <button
                onClick={() => setIsAccuracyModalOpen(false)}
                className="text-gray-300 hover:text-white"
                aria-label="Kapat"
              >
                ✕
              </button>
            </div>

            <div className="flex flex-col lg:flex-row justify-around items-center text-white space-y-4 lg:space-y-0 lg:space-x-4">
              {accuracyData.map((data, index) => (
                <CircularProgressBar
                  key={index}
                  percentage={data.percentage}
                  label={data.label}
                  className="text-white"
                />
              ))}
            </div>
            <p className="text-gray-400 text-sm mt-4 text-center">
              Doğruluk oranları yaklaşık değerlerdir ve görüntü kalitesi ile
              konum benzersizliğine bağlı olarak değişebilir.
            </p>
          </div>
        </div>
      )}

      {isNearbyPlacesModalOpen && responseData && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center z-50"
          onClick={() => setIsNearbyPlacesModalOpen(false)}
        >
          <div
            className="relative bg-gray-800 rounded-lg shadow-lg p-6 max-w-4xl w-full border border-gray-600"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute top-3 right-3 text-gray-300 hover:text-white focus:outline-none z-10"
              onClick={() => setIsNearbyPlacesModalOpen(false)}
            >
              <X className="w-8 h-8" />
            </button>
            <h3 className="text-2xl text-blue-300 font-semibold mb-4">
              Çevresindeki Mekânlar Haritası
            </h3>
            <div className="h-[60vh] w-full">
              <NearbyPlacesMap placeName={responseData.location} />
            </div>
          </div>
        </div>
      )}

      {isFoodMapModalOpen && responseData && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center z-50"
          onClick={() => setIsFoodMapModalOpen(false)}
        >
          <div
            className="relative bg-gray-800 rounded-lg shadow-lg p-6 max-w-4xl w-full h-[80vh] border border-gray-600 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-2xl text-orange-400 font-semibold flex items-center gap-2">
                  <Utensils className="w-7 h-7" />
                  <span>{responseData.location} Yakınındaki Lezzetler</span>
                </h3>
                <p className="text-gray-400 text-sm mt-1">
                  Google Haritalar verilerine göre en yüksek puanlı 3 mekan.
                </p>
              </div>
              <button
                className="text-gray-300 hover:text-white focus:outline-none p-1 rounded-full hover:bg-gray-700 transition-colors"
                onClick={() => setIsFoodMapModalOpen(false)}
              >
                <X className="w-8 h-8" />
              </button>
            </div>

            {/* Harita Alanı */}
            <div className="flex-1 w-full rounded-xl overflow-hidden border border-gray-700 relative">
               <NearbyFoodMap 
                 placeName={responseData.location} 
                 latitude={responseData.latitude} 
                 longitude={responseData.longitude}
               />
            </div>
          </div>
        </div>
      )}

      {/* YENİ EKLENEN TOKEN UYARI MODALI */}
          {tokenModal.isOpen && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
              <div 
                className="absolute inset-0 bg-black/70 backdrop-blur-sm" 
                onClick={() => setTokenModal({ isOpen: false, message: '', requiredAmount: 0 })}
              ></div>
              <div className="relative bg-gray-900 border border-gray-700/50 rounded-3xl shadow-2xl p-8 max-w-sm w-full text-center animate-in fade-in zoom-in duration-300">
                <div className="w-16 h-16 bg-red-500/20 text-red-400 rounded-full flex items-center justify-center mx-auto mb-4 border border-red-500/30">
                  <Wallet size={32} />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">Yetersiz Token</h3>
                <p className="text-gray-400 mb-6 leading-relaxed">
                  {tokenModal.message}
                  <br/>
                  <span className="text-sm font-semibold text-blue-400 mt-2 block">Mevcut Bakiyen: {userTokens || 0}</span>
                </p>
                <div className="flex flex-col gap-3">
                  <button
                    onClick={() => navigate('/pricing')} // Pricing sayfasına yönlendirir
                    className="w-full py-3 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl shadow-lg transition-all active:scale-95"
                  >
                    Token Satın Al
                  </button>
                  <button
                    onClick={() => setTokenModal({ isOpen: false, message: '', requiredAmount: 0 })}
                    className="w-full py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 font-semibold rounded-xl transition-all active:scale-95 border border-gray-700"
                  >
                    Vazgeç
                  </button>
                </div>
              </div>
            </div>
          )}
    </div>
  );
}

export default UploadForm;